import { FC, useEffect, useMemo } from 'react'
import { Box, Icon } from '@chakra-ui/react'
import { useSearchParams } from 'react-router-dom'
import {
  FiUser,
  FiStar,
  FiUsers,
  FiBriefcase,
  FiMapPin,
  FiFolder,
  FiActivity,
  FiSettings,
} from 'react-icons/fi'
import Page from '../../components/Page'
import PrescriberProfilePage from '../PrescriberProfilePage/PrescriberProfilePage'
import OrganizationProfilePage from '../OrganizationProfilePage'
import { SubscriptionInfo } from '../Billing/SubscriptionInfo'
import { RootState } from '../../app/store'
import { useAppSelector } from '../../app/typedReduxHooks'
import { useHasPermission } from '../../hooks/useHasPermission'
import { OrganizationAdminProfilePage } from '../../features/organization-admin/OrganizationAdminProfilePage'
import { UsersPage } from '../../features/users/UsersPage'
import UnauthorizedPage from '../../components/UnauthorizedPage'
import { IntegrationsPage } from '../../features/integration/IntegrationsPage'
import { LocationsPage } from '../../features/locations/LocationsPage'
import { useFeatureToggle } from '../../common/utils/feature-toggle/useFeatureToggle'
import { useHasEntitlement } from '../OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../../types/organization-entitlements'
import { PlanCode } from '../../types/subscription'
import {
  SETTINGS_NAV_COLLAPSED_STORAGE_KEY,
  SidePanelLayout,
  SidePanelNav,
  SidePanelNavGroup,
  useIsDesktopNav,
  useSidePanelNavCollapse,
} from '../../common/components/SidePanelNav'
import { AppPageContent, AppPageHeader } from '../../common/components/AppPage'

import { IconType } from 'react-icons'
import { Permission } from '../../types/permission'
import { AnalyticsAction } from '../../common/utils/analytics'

interface TabConfig {
  id: string
  label: string
  action: AnalyticsAction
  component: React.ReactNode
  icon: IconType
  isVisible: boolean
  group: 'personal' | 'workspace' | 'billing'
}

const GROUP_LABELS: Record<TabConfig['group'], string> = {
  personal: 'Personal',
  workspace: 'Workspace',
  billing: 'Billing & Tools',
}

const SettingsPage: FC<unknown> = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const isDesktopNav = useIsDesktopNav()
  const { isCollapsed, toggleCollapse } = useSidePanelNavCollapse(
    SETTINGS_NAV_COLLAPSED_STORAGE_KEY
  )

  const authInfo = useAppSelector((state: RootState) => state.authentication)
  const loginFinished = authInfo.loginFinished
  const permissions = authInfo?.permissions ?? []
  const organizationId = useAppSelector((state: RootState) => state.organization?.data?.id)
  const organizationSettings = useAppSelector((state) => state.organization?.data?.settings)
  const isPrescriberActive = useAppSelector((state) => state.prescriber.prescriber?.active)
  const organizationAdmin = useAppSelector((state) => state.organizationAdmin.profile)
  const isAdminActive = organizationAdmin?.active
  const subscription = useAppSelector((state: RootState) => state.billing?.subscription.data)
  const organizationPlanCode = useAppSelector(
    (state: RootState) => state.organization?.entitlements?.planCode
  )

  const hasLocationManagementPermission = useHasPermission(Permission.LOCATION_MANAGEMENT, {
    includeOwnerSuperAdminFallback: true,
  })
  const hasBillingManagementPermission = useHasPermission(Permission.BILLING_MANAGEMENT, {
    includeOwnerSuperAdminFallback: true,
  })

  const isAbleToManageBilling =
    subscription?.stripeSubscriptionId &&
    organizationPlanCode !== PlanCode.UNLIMITED_ONE_YEAR &&
    hasBillingManagementPermission

  const { isFeatureEnabled: isIntegrationsFeatureOn } = useFeatureToggle('INTEGRATIONS')
  const hasMultipleLocations = useHasEntitlement(FeatureKey.MULTIPLE_LOCATIONS)

  const tabConfigs: TabConfig[] = useMemo(
    () => [
      {
        id: 'prescriber',
        label: 'Prescriber Profile',
        action: 'settings_tab_prescriber',
        component: <PrescriberProfilePage />,
        icon: FiUser,
        isVisible: loginFinished && !!isPrescriberActive,
        group: 'personal',
      },
      {
        id: 'admin',
        label: 'Admin Profile',
        action: 'settings_tab_admin',
        component: <OrganizationAdminProfilePage />,
        icon: FiStar,
        isVisible: loginFinished && !!isAdminActive,
        group: 'personal',
      },
      {
        id: 'users',
        label: 'Users',
        action: 'settings_tab_users',
        component: <UsersPage />,
        icon: FiUsers,
        isVisible: loginFinished && !!organizationSettings?.enableUsersBoard,
        group: 'workspace',
      },
      {
        id: 'organization',
        label: 'Organisation',
        action: 'settings_tab_organization',
        component: <OrganizationProfilePage />,
        icon: FiBriefcase,
        isVisible: loginFinished && !!organizationSettings?.enableUpdateOrganization,
        group: 'workspace',
      },
      {
        id: 'locations',
        label: 'Locations',
        action: 'settings_tab_locations',
        component: <LocationsPage />,
        icon: FiMapPin,
        isVisible: loginFinished && hasMultipleLocations && hasLocationManagementPermission,
        group: 'workspace',
      },
      {
        id: 'subscription',
        label: 'Subscription',
        action: 'settings_tab_subscription',
        component: <SubscriptionInfo subscription={subscription} />,
        icon: FiFolder,
        isVisible: loginFinished && !!isAbleToManageBilling,
        group: 'billing',
      },
      {
        id: 'integrations',
        label: 'Integrations',
        action: 'settings_tab_integrations',
        component: <IntegrationsPage />,
        icon: FiActivity,
        isVisible:
          loginFinished &&
          !!organizationSettings?.enableAutoBuildIntegration &&
          isIntegrationsFeatureOn,
        group: 'billing',
      },
    ],
    [
      loginFinished,
      isPrescriberActive,
      isAdminActive,
      organizationSettings?.enableUsersBoard,
      organizationSettings?.enableUpdateOrganization,
      organizationSettings?.enableAutoBuildIntegration,
      isAbleToManageBilling,
      subscription,
      hasMultipleLocations,
      hasLocationManagementPermission,
      hasBillingManagementPermission,
      isIntegrationsFeatureOn,
    ]
  )

  const visibleTabs = useMemo(() => tabConfigs.filter((tab) => tab.isVisible), [tabConfigs])

  const navGroups = useMemo((): SidePanelNavGroup[] => {
    const groupOrder: TabConfig['group'][] = ['personal', 'workspace', 'billing']

    return groupOrder
      .map((groupKey): SidePanelNavGroup | null => {
        const items = visibleTabs
          .filter((tab) => tab.group === groupKey)
          .map(({ id, label, icon, action }) => ({
            id,
            label,
            icon: <Icon as={icon} boxSize="16px" />,
            action,
          }))

        if (items.length === 0) {
          return null
        }

        return {
          label: GROUP_LABELS[groupKey],
          items,
        }
      })
      .filter((group): group is SidePanelNavGroup => group !== null)
  }, [visibleTabs])

  const currentTab = searchParams.get('tab')
  const activeTab = visibleTabs.find((tab) => tab.id === currentTab) ?? visibleTabs[0]

  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId })
  }

  useEffect(() => {
    if (visibleTabs.length === 0) {
      return
    }

    const currentTabIndex = visibleTabs.findIndex((tab) => tab.id === currentTab)

    if (currentTabIndex !== -1) {
      return
    }

    const requestedTab = searchParams.get('tab')

    const handleTabWithLoadingDependencies = (tabId: string) => {
      if (tabId === 'subscription') {
        if (!organizationAdmin && loginFinished) {
          return true
        }

        if (!organizationId && loginFinished) {
          return true
        }

        if (hasBillingManagementPermission && organizationId && !subscription) {
          return true
        }

        if (hasBillingManagementPermission && subscription?.stripeSubscriptionId) {
          return true
        }
      }

      if (['users', 'organization', 'locations', 'integrations'].includes(tabId)) {
        if (!organizationId && loginFinished) {
          return true
        }

        if (!organizationSettings && loginFinished && organizationId) {
          return true
        }

        const tabShouldBeVisible = {
          users: organizationSettings?.enableUsersBoard,
          organization: organizationSettings?.enableUpdateOrganization,
          integrations: organizationSettings?.enableAutoBuildIntegration,
          locations: hasMultipleLocations && hasLocationManagementPermission,
        }[tabId]

        if (tabShouldBeVisible) {
          return true
        }
      }

      return false
    }

    if (requestedTab && handleTabWithLoadingDependencies(requestedTab)) {
      return
    }

    const defaultTab =
      visibleTabs.find((tab) => ['prescriber', 'admin'].includes(tab.id)) || visibleTabs[0]

    if (defaultTab) {
      setSearchParams({ tab: defaultTab.id })
    }
  }, [
    currentTab,
    visibleTabs,
    setSearchParams,
    searchParams,
    subscription,
    organizationId,
    organizationAdmin,
    organizationSettings,
    loginFinished,
    hasBillingManagementPermission,
    hasMultipleLocations,
    hasLocationManagementPermission,
  ])

  if (permissions.length === 0) {
    return <UnauthorizedPage />
  }

  return (
    <Page>
      <AppPageContent>
        <AppPageHeader
          title="Settings"
          subtitle="Manage your profile, workspace, and billing preferences"
          icon={<Icon as={FiSettings} boxSize="18px" color="white" aria-hidden />}
        />

        <SidePanelLayout
          isCollapsed={isCollapsed}
          isMobileView={!isDesktopNav}
          nav={
            <SidePanelNav
              activeId={activeTab?.id ?? ''}
              groups={navGroups}
              isCollapsed={isCollapsed}
              onToggleCollapse={toggleCollapse}
              onChange={handleTabChange}
              ariaLabel="Settings navigation"
              isMobileView={!isDesktopNav}
            />
          }
        >
          <Box>{activeTab?.component}</Box>
        </SidePanelLayout>
      </AppPageContent>
    </Page>
  )
}

export default SettingsPage
